import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Target, Heart, Rocket, Zap, Users, BookOpen } from "lucide-react";

const WhyChooseSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const cards = [
    {
      icon: Target,
      title: "Frustré par des résultats limités ?",
      description:
        "Imaginez pouvoir offrir à vos patients des soins holistiques qui transforment réellement leur vie. La formation Quantic Revolution 4.0 est conçue pour briser ces barrières et vous permettre d'apporter des changements profonds et durables.",
      color: "purple",
      span: "lg:col-span-7",
    },
    {
      icon: Heart,
      title: "Besoin de méthodes holistiques ?",
      description:
        "Grâce à notre approche quantique et énergétique, vous serez en mesure d'intervenir de manière systémique et holistique, apportant une guérison véritable à vos patients.",
      color: "cyan",
      span: "lg:col-span-5",
    },
    {
      icon: Rocket,
      title: "Désir de se démarquer ?",
      description:
        "Devenez un thérapeute révolutionnaire capable d'intervenir de manière systémique et ajustée. En intégrant des compétences avancées, vous vous démarquerez dans un marché saturé.",
      color: "gold",
      span: "lg:col-span-5",
    },
    {
      icon: Zap,
      title: "Formation Complète et Innovante",
      description:
        "À la pointe des techniques de guérison quantique et énergétique. Les 24 modules comprennent des sessions de pratique et des masterclass exclusives, totalisant 48 heures de contenu transformateur.",
      color: "purple",
      span: "lg:col-span-7",
    },
  ];

  const features = [
    {
      icon: Users,
      text: "Communauté Active de praticiens",
    },
    {
      icon: BookOpen,
      text: "Ebooks et protocoles exclusifs",
    },
    {
      icon: Zap,
      text: "Outils vibratoires innovants",
    },
  ];

  const getColorClasses = (color) => {
    switch (color) {
      case "cyan":
        return {
          border: "border-cyan-500/30",
          bg: "bg-cyan-500/10",
          text: "text-cyan-400",
          glow: "group-hover:shadow-cyan-500/20",
        };
      case "gold":
        return {
          border: "border-amber-500/30",
          bg: "bg-amber-500/10",
          text: "text-amber-400",
          glow: "group-hover:shadow-amber-500/20",
        };
      default:
        return {
          border: "border-purple-500/30",
          bg: "bg-purple-500/10",
          text: "text-purple-400",
          glow: "group-hover:shadow-purple-500/20",
        };
    }
  };

  return (
    <section
      ref={ref}
      data-testid="why-choose-section"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #030014 0%, #090920 100%)" }}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-cyan-400 text-sm uppercase tracking-[0.2em] mb-4 block">
            Pourquoi nous choisir
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Pourquoi choisir{" "}
            <span className="text-gradient-cosmic">Quantic Revolution 4.0</span> ?
          </h2>
          <p className="max-w-2xl mx-auto text-slate-400 text-lg">
            Une formation conçue pour briser les barrières et transformer
            véritablement votre pratique thérapeutique.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {cards.map((card, index) => {
            const colors = getColorClasses(card.color);
            const Icon = card.icon;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className={`${card.span} group`}
              >
                <div
                  className={`glass-card h-full p-8 rounded-2xl card-hover ${colors.border} ${colors.glow} group-hover:shadow-2xl`}
                >
                  <div
                    className={`w-14 h-14 rounded-xl ${colors.bg} flex items-center justify-center mb-6`}
                  >
                    <Icon className={`w-7 h-7 ${colors.text}`} />
                  </div>
                  <h3
                    className="text-2xl font-light text-white mb-4"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {card.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed">
                    {card.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Features Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 flex flex-wrap justify-center gap-6"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="flex items-center gap-3 px-6 py-3 rounded-full bg-indigo-950/30 border border-indigo-500/20"
              >
                <Icon className="w-5 h-5 text-cyan-400" />
                <span className="text-slate-300">{feature.text}</span>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
